/**
 * Session lifecycle events.
 *
 * A 401 can arrive from any request at any time, including one started outside a
 * React render. The API client cannot redirect on its own (it has no router and
 * no cache), so it publishes here and the app-level listener owns the reaction.
 * Keeping it a one-line pub/sub avoids an import cycle between `api`, the router
 * and the session store.
 */

type SessionExpiredListener = () => void

const listeners = new Set<SessionExpiredListener>()

/** Registers a listener; the returned function unregisters it. */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Announces that the server rejected the current credential as invalid.
 *
 * A listener that fails must not decide the fate of the others, and the caller
 * is `apiRequest` on its way to throwing the real `ApiError`: an exception here
 * would replace that error with an unrelated one, so every listener is isolated.
 */
export function notifySessionExpired(): void {
  for (const listener of listeners) {
    try {
      listener()
    } catch (error) {
      console.error('session expiry listener failed', { error: String(error) })
    }
  }
}
